import { DragOverlay, useDndContext } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { flexRender } from '@tanstack/react-table';

import { isHeaderDraggable } from '../../../shared/components/table-header/utils';
import type { TableHeaderProps } from '../../../shared/types';

import { SortableTableHeader } from './SortableTableHeader';

/**
 * Enhanced table header component with drag-and-drop column reordering.
 */
export const TableHeader = <TData,>({ tableHeaderGroups }: TableHeaderProps<TData>) => {
  const { active } = useDndContext();
  const activeId = active?.id ? String(active.id) : null;

  if (!tableHeaderGroups.length) {
    return null;
  }

  const headerGroup = tableHeaderGroups[0];
  const draggableHeaderIds = headerGroup.headers.filter(isHeaderDraggable).map((header) => header.id);

  const activeHeader = headerGroup.headers.find((header) => header.id === activeId);

  return (
    <>
      <thead
        id="table-header"
        className="bg-info-50 sticky top-0 z-20 grid"
        role="rowgroup"
        aria-label="Table headers with sortable and draggable columns"
      >
        {tableHeaderGroups.map((headerGroup) => (
          <tr key={headerGroup.id} className="flex w-full" role="row">
            <SortableContext items={draggableHeaderIds} strategy={horizontalListSortingStrategy}>
              {headerGroup.headers.map((header) => (
                <SortableTableHeader key={header.id} header={header} />
              ))}
            </SortableContext>
          </tr>
        ))}
      </thead>

      <DragOverlay>
        {activeId && activeHeader ? (
          <div
            className="bg-info-50 border-info-200 text-info-500 ring-primary-500/20 rounded border px-3 py-3.5 text-left text-sm font-normal shadow-2xl ring-2 backdrop-blur-sm"
            role="dialog"
            aria-label={`Dragging ${activeHeader.column.columnDef.header} column`}
            style={{ width: `${activeHeader.getSize()}px` }}
          >
            {flexRender(activeHeader.column.columnDef.header, activeHeader.getContext())}
          </div>
        ) : null}
      </DragOverlay>
    </>
  );
};
